import React from "react";
import { getProvidePostionFilter } from "../../lib/common-service";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { GetEventDataSourceAPI, GetEventTypeAPI, GetEventServiceLocationAPI, GetEventPositionAPI, GetEventCompPlanAPI } from "../../lib/common-service";

import {
  GET_TIME_PERIOD_DATA,
  GET_EVENT_AMOUNT_DATA,
  GET_EVENT_DASHBOARD_CARD_DATA,
  GET_EVENT_LANDING_DASHBOARD_DATA,
  UPDATE_EVENT_DASHBOARD_CHART_LAYOUT,
  GET_EVENT_DASHBOARD_AMOUNT_AND_TOTAL_AMOUNT_LAST_FIVE_BATCHES_DATA,
  GET_EVENT_DASHBOARD_TOTAL_LOAD_AND_ERROR_COUNT_DATA,
  GET_EVENT_DASHBOARD_PROCESSED_VS_NEW_RECORDS_DATA,
  GET_EVENT_DASHBOARD_AMOUNT_BY_DATA_SOURCE_TREND_DATA,
  GET_EVENT_DASHBOARD_EVENT_TOTAL_AMOUNT_TREND_ROLLING_DATA,
  GET_EVENT_DASHBOARD_TOTAL_AMOUNT_BY_DATA_SOURCE_TREND_DATA,
  GET_EVENT_DASHBOARD_AMOUNT_AND_TOTAL_AMOUNT_BY_DATA_SOURCE_TREND_DATA,
  GET_EVENT_DASHBOARD_TOP_POSITIONS_BY_EVENT_AMOUNT_DATA,
} from "../../api-setup/api-endpoints";
import { POSTAPI, errStatusHandler, generateHeader } from "../../api-setup/api-helper";
import { getTenantCookieData, getUserCredential } from "../../lib/cookies";
import { setNewMessage } from "../../redux/actions/setting-actions";
import { GridColumnDataMapper } from "../../utils/utils";
import { CHART_COLOR_PALETTE, PROCARE_COLORS } from "src/constants/constants";

const EMPTY_EVENT_DASHBOARD_CARD_METRICS = {
  total_records: 0,
  new_records: 0,
  processed_records: 0,
  unassigned_records: 0,
};

const toCardMetricNumber = (raw) => {
  if (raw === null || raw === undefined || raw === "") return 0;
  const n = Number(String(raw).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const mapRowToEventDashboardCardMetrics = (row) => {
  if (!row || typeof row !== "object") {
    return { ...EMPTY_EVENT_DASHBOARD_CARD_METRICS };
  }

  return {
    total_records: toCardMetricNumber(row.total_records ?? row.Total_Records ?? row.totalrecords),
    new_records: toCardMetricNumber(row.new_records ?? row.New_Records),
    processed_records: toCardMetricNumber(row.processed_records ?? row.Processed_Records),
    unassigned_records: toCardMetricNumber(row.unassigned_records ?? row.Unassigned_Records),
  };
};

export default function useEventDashboardData() {
  const cookiesData = getUserCredential();
  const dispatch = useDispatch();
  const history = useHistory();
  const header = {
    "x-access-token": cookiesData.secretToken,
  };

  const [eventCategories, setEventCategories] = React.useState([]);
  const [eventSeries, setEventSeries] = React.useState([]);
  const [isEventChartLoading, setIsEventChartLoading] = React.useState(false);
  const [noEventData, setNoEventData] = React.useState(false);
  const [eventLegendData, setEventLegendData] = React.useState([]);
  const [eventPointVisibility, setEventPointVisibility] = React.useState([]);

  const [eventAmountCategories, setEventAmountCategories] = React.useState([]);
  const [eventAmountSeries, setEventAmountSeries] = React.useState([]);
  const [isEventAmountChartLoading, setIsEventAmountChartLoading] = React.useState(false);
  const [noEventAmountData, setNoEventAmountData] = React.useState(false);
  const [eventAmountLegendData, setEventAmountLegendData] = React.useState([]);
  const [eventAmountPointVisibility, setEventAmountPointVisibility] = React.useState([]);
  const [originalTimePeriodData, setOriginalTimePeriodData] = React.useState([]);
  const [timePeriodData, setTimePeriodData] = React.useState({
    timePeriodValue: {
      in_time_period: "",
    },
    timePeriodDataValue: [],
    istimePeriodDataLoading: true,
  });
  const getTimePeriodData = async (listData) => {
    try {
      const res = await POSTAPI(false, GET_TIME_PERIOD_DATA, null, header, listData, dispatch);
      if (res.status) {
        if (res.data.out_parameters.out_ret_code > 0) {
          setTimePeriodData({
            ...timePeriodData,
            istimePeriodDataLoading: false,
          });
          return null;
        } else {
          let tempArray = res.data.row_result.map((item) => ({
            text: item.time_period_name + (item.time_period_label ? `(${item.time_period_label})` : ""),
            value: item.time_period_start_date + " - " + item.time_period_end_date,
          }));

          setOriginalTimePeriodData(tempArray);
          setTimePeriodData({
            ...timePeriodData,
            timePeriodValue: tempArray[0],
            timePeriodDataValue: tempArray,
            istimePeriodDataLoading: false,
          });

          return tempArray;
        }
      }
    } catch (error) {
      console.error("Error fetching time period data:", error);
      return null;
    }
  };
  const getEventAmountChartData = (data) => {
    return new Promise((resolve, reject) => {
      setIsEventAmountChartLoading(true);

      POSTAPI(false, GET_EVENT_AMOUNT_DATA, null, header, data, dispatch)
        .then((res) => {
          const { out_ret_code } = res.data.out_parameters || {};
          const rowResult = res.data.row_result || [];

          if ((out_ret_code === 0 || out_ret_code === 2) && rowResult.length) {
            const colorPalette = CHART_COLOR_PALETTE;

            const categories = [];
            const data = [];
            const colors = [];
            const visibility = [];

            const legendData = rowResult.map((item, index) => {
              categories.push(item.category_axis);
              data.push(Number(item.series_data) || 0);
              colors.push(colorPalette[index % colorPalette.length]);
              visibility.push(true);

              return {
                name: item.category_axis,
                color: colorPalette[index % colorPalette.length],
                visible: true,
                index,
              };
            });

            setEventAmountCategories(categories);
            setEventAmountSeries([
              {
                name: "Event Count",
                data,
                color: colors,
              },
            ]);
            setEventAmountPointVisibility(visibility);
            setEventAmountLegendData(legendData);
            setNoEventAmountData(false);
          } else {
            setEventAmountCategories([]);
            setEventAmountSeries([]);
            setEventAmountLegendData([]);
            setNoEventAmountData(true);
          }

          setIsEventAmountChartLoading(false);
          resolve(rowResult);
        })
        .catch((error) => {
          console.error("Error in getEventTypeChartData:", error);
          setIsEventChartLoading(false);
          setNoEventData(true);
          reject(error);
        });
    });
  };
  const getEventTypeDataList = (dataObjValue) => {
    return GetEventTypeAPI(dataObjValue, dispatch, history);
  };
  const getEventDataSourceDataList = (dataObjValue) => {
    return GetEventDataSourceAPI(dataObjValue, dispatch, history);
  };
  const getEventServiceLocationDataList = (dataObjValue) => {
    return GetEventServiceLocationAPI(dataObjValue, dispatch, history);
  };
  const getEventPositionDataList = (dataObjValue) => {
    return GetEventPositionAPI(dataObjValue, dispatch, history);
  };
  const getEventCompPlanDataList = (dataObjValue) => {
    return GetEventCompPlanAPI(dataObjValue, dispatch, history);
  };
  const getLandingDashboardData = (dataObjValue) => {
    return POSTAPI(false, GET_EVENT_LANDING_DASHBOARD_DATA, null, generateHeader(), dataObjValue, dispatch);
  };
  const getEventDashboardCardData = (dataObjValue) => {
    return new Promise((resolve, reject) => {
      POSTAPI(false, GET_EVENT_DASHBOARD_CARD_DATA, null, header, dataObjValue, dispatch)
        .then((res) => {
          const envelope = res?.data !== undefined ? res.data : res;
          const payload =
            envelope?.data && envelope?.data?.row_result !== undefined ? envelope.data : envelope;
          const { out_ret_code } = payload.out_parameters || {};

          if (out_ret_code === 0 || out_ret_code === 2) {
            const row = Array.isArray(payload.row_result) ? payload.row_result[0] : null;
            resolve(mapRowToEventDashboardCardMetrics(row));
            return;
          }

          resolve({ ...EMPTY_EVENT_DASHBOARD_CARD_METRICS });
        })
        .catch((error) => {
          reject(error);
        });
    });
  };
  const updateChartLayout = (dataObjValue) => {
    return POSTAPI(false, UPDATE_EVENT_DASHBOARD_CHART_LAYOUT, null, generateHeader(), dataObjValue, dispatch);
  };
  const getEventDashboardAmountAndTotalAmountLastFiveBatchesData = (dataObjValue) => {
    return POSTAPI(
      false,
      GET_EVENT_DASHBOARD_AMOUNT_AND_TOTAL_AMOUNT_LAST_FIVE_BATCHES_DATA,
      null,
      header,
      dataObjValue,
      dispatch
    );
  };
  const getEventDashboardTotalLoadAndErrorCountData = (dataObjValue) => {
    return POSTAPI(false, GET_EVENT_DASHBOARD_TOTAL_LOAD_AND_ERROR_COUNT_DATA, null, header, dataObjValue, dispatch);
  };
  const getEventDashboardProcessedVsNewRecordsData = (dataObjValue) => {
    return POSTAPI(false, GET_EVENT_DASHBOARD_PROCESSED_VS_NEW_RECORDS_DATA, null, header, dataObjValue, dispatch);
  };
  const getEventDashboardAmountByDataSourceTrendData = (dataObjValue) => {
    return POSTAPI(false, GET_EVENT_DASHBOARD_AMOUNT_BY_DATA_SOURCE_TREND_DATA, null, header, dataObjValue, dispatch);
  };
  const getEventDashboardEventTotalAmountTrendRollingData = (dataObjValue) => {
    return POSTAPI(
      false,
      GET_EVENT_DASHBOARD_EVENT_TOTAL_AMOUNT_TREND_ROLLING_DATA,
      null,
      header,
      dataObjValue,
      dispatch
    );
  };
  const getEventDashboardTotalAmountByDataSourceTrendData = (dataObjValue) => {
    return POSTAPI(
      false,
      GET_EVENT_DASHBOARD_TOTAL_AMOUNT_BY_DATA_SOURCE_TREND_DATA,
      null,
      header,
      dataObjValue,
      dispatch
    );
  };
  const getEventDashboardAmountAndTotalAmountByDataSourceTrendData = (dataObjValue) => {
    return POSTAPI(
      false,
      GET_EVENT_DASHBOARD_AMOUNT_AND_TOTAL_AMOUNT_BY_DATA_SOURCE_TREND_DATA,
      null,
      header,
      dataObjValue,
      dispatch
    );
  };
  const getEventDashboardTopPositionsByEventAmountData = (dataObjValue) => {
    return POSTAPI(
      false,
      GET_EVENT_DASHBOARD_TOP_POSITIONS_BY_EVENT_AMOUNT_DATA,
      null,
      header,
      dataObjValue,
      dispatch
    );
  };

  return [
    {
      cookiesData,
      eventCategories,
      eventSeries,
      isEventChartLoading,
      noEventData,
      setEventSeries,
      eventLegendData,
      setEventLegendData,
      eventPointVisibility,
      setEventPointVisibility,
      timePeriodData,
      setTimePeriodData,
      isEventAmountChartLoading,
      eventAmountCategories,
      eventAmountSeries,
      noEventAmountData,
      eventAmountLegendData,
      eventAmountPointVisibility,
      setEventAmountCategories,
      setEventAmountSeries,
      setEventAmountLegendData,
      setEventAmountPointVisibility,
    },
    {
      getTimePeriodData,
      getEventAmountChartData,
      getEventTypeDataList,
      getEventDataSourceDataList,
      getLandingDashboardData,
      getEventDashboardCardData,
      updateChartLayout,
      getEventServiceLocationDataList,
      getEventPositionDataList,
      getEventCompPlanDataList,
      getEventDashboardAmountAndTotalAmountLastFiveBatchesData,
      getEventDashboardTotalLoadAndErrorCountData,
      getEventDashboardProcessedVsNewRecordsData,
      getEventDashboardAmountByDataSourceTrendData,
      getEventDashboardEventTotalAmountTrendRollingData,
      getEventDashboardTotalAmountByDataSourceTrendData,
      getEventDashboardAmountAndTotalAmountByDataSourceTrendData,
      getEventDashboardTopPositionsByEventAmountData,
    },
  ];
}
